import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import {
  formatYearMonthLabel,
  parseYearMonthKey,
  shiftYearMonthKey,
} from '../../utils/period';
import './styles.css';

const CONTEXT_OFFSETS = Object.freeze([-2, -1, 0, 1, 2]);
const SWIPE_OFFSETS = Object.freeze([-1, 0, 1]);
const SWIPE_THRESHOLD_RATIO = 0.35;
const SWIPE_MIN_FLICK_DISTANCE = 48;
const SWIPE_MIN_VELOCITY = 0.55;
const SWIPE_DURATION_MS = 180;

const getContextClass = (offset) => {
  if (Math.abs(offset) === 2) return 'period-navigator__period--far';
  if (Math.abs(offset) === 1) return 'period-navigator__period--near';
  return 'period-navigator__period--selected';
};

const PeriodNavigator = ({ value, currentPeriod, onChange }) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const gestureRef = useRef(null);
  const settleTimerRef = useRef(null);
  const suppressClickRef = useRef(false);

  useEffect(() => () => {
    if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current);
  }, []);

  if (!parseYearMonthKey(value) || !parseYearMonthKey(currentPeriod)) {
    return null;
  }

  const selectOffset = (offset) => {
    const nextPeriod = shiftYearMonthKey(value, offset);
    if (nextPeriod) onChange(nextPeriod);
  };

  const resetGesture = () => {
    gestureRef.current = null;
    setDragging(false);
  };

  const settleSwipe = (direction = 0, width = 0) => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const complete = () => {
      if (direction) selectOffset(direction);
      setSettling(false);
      setDragOffset(0);
    };

    if (reducedMotion) {
      complete();
      return;
    }

    setSettling(true);
    setDragOffset(direction ? -direction * width : 0);
    settleTimerRef.current = window.setTimeout(complete, SWIPE_DURATION_MS);
  };

  const handlePointerDown = (event) => {
    const mobileViewport = window.matchMedia?.('(max-width: 767px)').matches;
    if (!mobileViewport || event.pointerType === 'mouse' || settling) return;

    if (settleTimerRef.current) window.clearTimeout(settleTimerRef.current);
    const width = event.currentTarget.clientWidth;
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startTime: event.timeStamp,
      width,
      axis: null,
      dx: 0,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    setDragOffset(0);
  };

  const handlePointerMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    gesture.dx = dx;

    if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) >= 8) {
      if (Math.abs(dx) > Math.abs(dy) * 1.15) gesture.axis = 'horizontal';
      else if (Math.abs(dy) > Math.abs(dx) * 1.15) gesture.axis = 'vertical';
    }

    if (gesture.axis === 'vertical') {
      setDragging(false);
      setDragOffset(0);
      return;
    }
    if (gesture.axis !== 'horizontal') return;

    event.preventDefault();
    suppressClickRef.current = true;
    const resistanceStart = gesture.width * 0.45;
    const magnitude = Math.abs(dx);
    const resistedMagnitude = magnitude > resistanceStart
      ? resistanceStart + (magnitude - resistanceStart) * 0.35
      : magnitude;
    setDragOffset(Math.sign(dx) * resistedMagnitude);
  };

  const finishPointerGesture = (event, cancelled = false) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const elapsed = Math.max(event.timeStamp - gesture.startTime, 1);
    const distance = Math.abs(gesture.dx);
    const velocity = distance / elapsed;
    const crossedDistance = distance >= gesture.width * SWIPE_THRESHOLD_RATIO;
    const clearFlick = distance >= SWIPE_MIN_FLICK_DISTANCE && velocity >= SWIPE_MIN_VELOCITY;
    const direction = !cancelled && gesture.axis === 'horizontal' && (crossedDistance || clearFlick)
      ? (gesture.dx < 0 ? 1 : -1)
      : 0;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    resetGesture();
    settleSwipe(direction, gesture.width);
    window.setTimeout(() => { suppressClickRef.current = false; }, 0);
  };

  const handlePeriodClick = (event, offset) => {
    if (suppressClickRef.current) {
      event.preventDefault();
      return;
    }
    selectOffset(offset);
  };

  return (
    <section className="period-navigator" aria-label="Navegação por período">
      <div className="period-navigator__control">
        <button
          type="button"
          className="period-navigator__arrow"
          onClick={() => selectOffset(-1)}
          aria-label="Mês anterior"
        >
          <PiCaretLeftBold size={18} aria-hidden="true" />
        </button>

        <div
          className={`period-navigator__mobile-viewport${dragging ? ' is-dragging' : ''}${settling ? ' is-settling' : ''}`}
          style={{ '--period-drag-x': `${dragOffset}px` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointerGesture}
          onPointerCancel={(event) => finishPointerGesture(event, true)}
          aria-label="Deslize horizontalmente para trocar o mês"
          data-testid="period-swipe-region"
        >
          <div className="period-navigator__mobile-track">
            {SWIPE_OFFSETS.map((offset) => {
              const period = shiftYearMonthKey(value, offset);
              const selected = offset === 0;
              return (
                <button
                  type="button"
                  key={period}
                  className={`period-navigator__period period-navigator__period--mobile${selected ? ' period-navigator__period--selected' : ''}`}
                  onClick={(event) => handlePeriodClick(event, offset)}
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  {formatYearMonthLabel(period)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="period-navigator__periods">
          {CONTEXT_OFFSETS.map((offset) => {
            const period = shiftYearMonthKey(value, offset);
            const selected = offset === 0;
            const fullLabel = formatYearMonthLabel(period);
            const visibleLabel = formatYearMonthLabel(period, { includeYear: selected });

            return (
              <button
                type="button"
                key={period}
                className={`period-navigator__period ${getContextClass(offset)}`}
                onClick={() => selectOffset(offset)}
                aria-label={selected ? `Período selecionado: ${fullLabel}` : `Selecionar ${fullLabel}`}
                aria-current={selected ? 'date' : undefined}
              >
                {visibleLabel}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="period-navigator__arrow"
          onClick={() => selectOffset(1)}
          aria-label="Próximo mês"
        >
          <PiCaretRightBold size={18} aria-hidden="true" />
        </button>
      </div>

      {value !== currentPeriod && (
        <button
          type="button"
          className="period-navigator__today"
          onClick={() => onChange(currentPeriod)}
        >
          Hoje
        </button>
      )}
    </section>
  );
};

PeriodNavigator.propTypes = {
  value: PropTypes.string.isRequired,
  currentPeriod: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PeriodNavigator;